// Synthesized metronome click timbres. Every sound here is generated as raw
// samples (no samples/recordings loaded from disk — keeps the app's "no
// external assets" property and works fully offline from first install).
//
// Each generator only ever writes into a mono Float32Array of samples; the
// caller (buildClickBuffer in stereoRouting.ts) is solely responsible for
// placing that mono signal into the RIGHT channel of a stereo buffer whose
// LEFT channel is separately zero-filled. Keeping the left-channel-silence
// guarantee in exactly one place (stereoRouting.ts) is what makes it
// trivially testable — sound generators here never see or touch a stereo
// buffer at all.

export type ClickSound = 'beep' | 'woodblock' | 'click' | 'cowbell';

export const CLICK_SOUND_LABELS: Record<ClickSound, string> = {
  beep: 'Digital Beep',
  woodblock: 'Woodblock',
  click: 'Click / Tick',
  cowbell: 'Cowbell',
};

export interface ClickSoundOptions {
  sound: ClickSound;
  /** true for the beat-1 accent hit, false for a regular beat/subdivision. */
  isAccent: boolean;
  /** true for a subdivision tick (softer/shorter than the main beat). */
  isSubdivision: boolean;
  sampleRate: number;
  gain: number;
}

export interface GeneratedClick {
  samples: Float32Array;
  durationSeconds: number;
}

export function generateClickSamples(options: ClickSoundOptions): GeneratedClick {
  switch (options.sound) {
    case 'woodblock':
      return generateWoodblock(options);
    case 'click':
      return generateClickTick(options);
    case 'cowbell':
      return generateCowbell(options);
    case 'beep':
    default:
      return generateBeep(options);
  }
}

function generateBeep(options: ClickSoundOptions): GeneratedClick {
  const { isAccent, isSubdivision, sampleRate, gain } = options;
  const durationSeconds = 0.045;
  const frameCount = Math.max(1, Math.round(durationSeconds * sampleRate));
  const samples = new Float32Array(frameCount);
  const frequency = isAccent ? 1500 : isSubdivision ? 700 : 1000;
  const amplitude = isSubdivision ? 0.55 * gain : gain;

  for (let i = 0; i < frameCount; i++) {
    const t = i / sampleRate;
    const envelope = Math.exp(-t * 40);
    samples[i] = Math.sin(2 * Math.PI * frequency * t) * envelope * amplitude;
  }
  return { samples, durationSeconds };
}

function generateWoodblock(options: ClickSoundOptions): GeneratedClick {
  const { isAccent, isSubdivision, sampleRate, gain } = options;
  const durationSeconds = 0.05;
  const frameCount = Math.max(1, Math.round(durationSeconds * sampleRate));
  const samples = new Float32Array(frameCount);
  // A woodblock reads as a short, resonant knock: a fast-decaying sine at a
  // low-mid "body" frequency plus a touch of higher-frequency "crack" noise
  // right at the transient, rather than a pure tone. Body+crack amplitudes
  // are chosen so their sum never exceeds 1 at t=0 (their simultaneous peak),
  // and the accent is an explicit amplitude boost, not just a pitch change,
  // so accent hits are reliably louder than regular hits.
  const bodyFrequency = isAccent ? 950 : 850;
  const accentBoost = isAccent ? 1 : 0.75;
  const amplitude = (isSubdivision ? 0.5 : 1) * accentBoost * gain;

  for (let i = 0; i < frameCount; i++) {
    const t = i / sampleRate;
    const bodyEnvelope = Math.exp(-t * 90);
    const crackEnvelope = Math.exp(-t * 400);
    const body = Math.sin(2 * Math.PI * bodyFrequency * t) * bodyEnvelope * 0.75;
    const crack = (pseudoRandom(i) * 2 - 1) * crackEnvelope * 0.25;
    samples[i] = (body + crack) * amplitude;
  }
  return { samples, durationSeconds };
}

function generateClickTick(options: ClickSoundOptions): GeneratedClick {
  const { isAccent, isSubdivision, sampleRate, gain } = options;
  const durationSeconds = 0.02;
  const frameCount = Math.max(1, Math.round(durationSeconds * sampleRate));
  const samples = new Float32Array(frameCount);
  // A very short, sharp transient (mostly filtered noise) rather than a
  // tone — reads as a crisp "tick," like a DAW metronome or stick click.
  const amplitude = (isSubdivision ? 0.5 : 1) * (isAccent ? 1 : 0.85) * gain;

  for (let i = 0; i < frameCount; i++) {
    const t = i / sampleRate;
    const envelope = Math.exp(-t * 700);
    samples[i] = (pseudoRandom(i) * 2 - 1) * envelope * amplitude;
  }
  return { samples, durationSeconds };
}

function generateCowbell(options: ClickSoundOptions): GeneratedClick {
  const { isAccent, isSubdivision, sampleRate, gain } = options;
  const durationSeconds = 0.12;
  const frameCount = Math.max(1, Math.round(durationSeconds * sampleRate));
  const samples = new Float32Array(frameCount);
  // Classic cowbell synthesis: two closely-spaced, inharmonic square-ish
  // tones (roughly a minor-second/tritone-ish pair) with a slower decay,
  // which is what gives a cowbell its characteristic clang rather than a
  // clean pitch.
  const f1 = isAccent ? 845 : 800;
  const f2 = f1 * 1.48;
  const accentBoost = isAccent ? 1 : 0.8;
  const amplitude = (isSubdivision ? 0.55 : 1) * accentBoost * gain;

  for (let i = 0; i < frameCount; i++) {
    const t = i / sampleRate;
    const envelope = Math.exp(-t * 18);
    const tone1 = Math.sign(Math.sin(2 * Math.PI * f1 * t));
    const tone2 = Math.sign(Math.sin(2 * Math.PI * f2 * t));
    samples[i] = ((tone1 + tone2) / 2) * envelope * amplitude * 0.6;
  }
  return { samples, durationSeconds };
}

/** Deterministic pseudo-random noise generator (no Math.random dependency, reproducible for tests). */
function pseudoRandom(seed: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}
